/**
 * Tokenización de tarjetas contra la API de Payway (`POST <apiUrl>/tokens`, API Key PÚBLICA en el
 * header `apikey`).
 *
 * Se llama a la API directo y no con el SDK `decidir.js`: con Cybersource deshabilitado el SDK
 * manda `device_unique_identifier` suelto, y Payway necesita `fraud_detection.device_unique_identifier`
 * (como tokeniza backend); sin él el pago termina en `not-approved`. Verificado con el Payload real
 * del SDK y con la request de backend (ENA-1607, 2026-10-14).
 *
 * Los datos de la tarjeta solo viajan en el body de esta llamada: no se guardan ni se registran.
 */
import { CardTokenizationError } from '../domain/errors';
import type { NewCardData, NewCardToken, PaymentToken } from '../domain/models';
import type { PaywayConfig } from '../config/paywayConfig';

/**
 * Datos de control de fraude al tokenizar. La IP del cliente NO la envía el front: backend la toma
 * de los headers del balanceador (`X-Forwarded-For`) y la manda a Payway.
 */
export interface FraudContext {
  deviceUniqueIdentifier: string;
}

export interface CardTokenizer {
  /** Tarjeta nueva: datos completos → token de pago (de un solo uso). */
  tokenizeNewCard(data: NewCardData): Promise<NewCardToken>;
  /**
   * Tarjeta guardada: token permanente de la tarjeta + CVV → token de pago NUEVO. Hay que generarlo
   * antes de cada `initiate`, incluso con tarjetas guardadas.
   */
  tokenizeSavedCard(cardToken: string, securityCode: string): Promise<PaymentToken>;
}

interface TokenResponse {
  id?: string;
  token?: string;
  bin?: string;
  last_four_digits?: string;
  cardholder?: { name?: string };
  validation_errors?: Array<{ param?: string }> | { param?: string; status?: string };
}

const REQUEST_TIMEOUT_MS = 15_000;
const DOCUMENT_TYPE = 'dni';

const digitsOnly = (value: string): string => value.replace(/\D/g, '');

const extractToken = (response: TokenResponse | null): string | null => {
  if (!response || typeof response !== 'object') {
    return null;
  }
  return typeof response.token === 'string'
    ? response.token
    : typeof response.id === 'string'
      ? response.id
      : null;
};

const extractField = (response: TokenResponse | null): string | undefined => {
  const errors = response?.validation_errors;
  if (Array.isArray(errors)) {
    return errors[0]?.param;
  }
  return errors?.param;
};

export const toTokenizationError = (
  statusCode: number,
  response: TokenResponse | null,
): CardTokenizationError => {
  if (statusCode === 0) {
    return new CardTokenizationError('timeout');
  }
  const field = extractField(response);
  if (statusCode >= 400 && statusCode < 500) {
    return new CardTokenizationError('invalid-card', { field });
  }
  return new CardTokenizationError('unknown', { field });
};

const withFraudDetection = (fraud?: FraudContext): Record<string, unknown> =>
  fraud ? { fraud_detection: { device_unique_identifier: fraud.deviceUniqueIdentifier } } : {};

const buildNewCardBody = (data: NewCardData, fraud?: FraudContext): Record<string, unknown> => ({
  card_number: digitsOnly(data.cardNumber),
  card_expiration_month: data.expirationMonth,
  card_expiration_year: data.expirationYear,
  security_code: digitsOnly(data.securityCode),
  card_holder_name: data.holderName.trim(),
  card_holder_identification: { type: DOCUMENT_TYPE, number: digitsOnly(data.documentNumber) },
  ...withFraudDetection(fraud),
});

// TODO(ENA-1607): confirmar con Payway/backend el formato exacto para el token de una tarjeta guardada
// (no está en el manual; este body es una suposición sin probar).
const buildSavedCardBody = (
  cardToken: string,
  securityCode: string,
  fraud?: FraudContext,
): Record<string, unknown> => ({
  token: cardToken,
  security_code: digitsOnly(securityCode),
  ...withFraudDetection(fraud),
});

// TODO(ENA-1607) [experimento, sin validar con un pago aprobado]: POST directo a `/tokens` en lugar del
// SDK `decidir.js`, con `fraud_detection.device_unique_identifier` como tokeniza backend. Validar:
// CORS por ambiente (hoy solo permite `Content-Type` y `apikey`), si Payway soporta esta llamada
// directa desde el navegador, y retirar `paywaySdkLoader`/`VITE_PAYWAY_SDK_URL` si queda definitivo.
export const createPaywayTokenizer = (
  getConfig: () => PaywayConfig,
  deps: {
    fetchImpl?: typeof fetch;
    getFraudContext?: () => FraudContext;
    timeoutMs?: number;
  } = {},
): CardTokenizer => {
  const fetchImpl: typeof fetch = deps.fetchImpl ?? ((...args) => fetch(...args));
  const timeoutMs = deps.timeoutMs ?? REQUEST_TIMEOUT_MS;

  /** `POST /tokens`: devuelve el token de pago y la respuesta de Payway. */
  const createToken = async (
    body: Record<string, unknown>,
  ): Promise<{ token: string; response: TokenResponse | null }> => {
    const config = getConfig();
    const url = `${config.apiUrl.replace(/\/+$/, '')}/tokens`;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let statusCode = 0;
    let response: TokenResponse | null = null;
    try {
      const result = await fetchImpl(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Solo estos headers: el preflight de Payway rechaza otros (p. ej. `Cache-Control`).
          apikey: config.publicApiKey,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      statusCode = result.status;
      response = (await result.json().catch(() => null)) as TokenResponse | null;
    } catch {
      // Timeout o corte de red: statusCode 0.
    } finally {
      clearTimeout(timer);
    }

    const token = extractToken(response);
    if ((statusCode === 200 || statusCode === 201) && token) {
      return { token, response };
    }

    throw toTokenizationError(statusCode, response);
  };

  return {
    tokenizeNewCard: async (data) => {
      const number = digitsOnly(data.cardNumber);
      const { token, response } = await createToken(buildNewCardBody(data, deps.getFraudContext?.()));
      return {
        token,
        bin: response?.bin || number.slice(0, 6),
        lastFourDigits: response?.last_four_digits || number.slice(-4),
        holderName: response?.cardholder?.name?.trim() || undefined,
      };
    },

    tokenizeSavedCard: async (cardToken, securityCode) => {
      const { token } = await createToken(
        buildSavedCardBody(cardToken, securityCode, deps.getFraudContext?.()),
      );
      return { token };
    },
  };
};
